import { Component, DestroyRef, OnInit, inject, signal, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption, MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, debounceTime, lastValueFrom, map, of, startWith, switchMap } from 'rxjs';
import { EmployeeCalendarRegisterRelatableType } from 'src/app/enums/employee-calendar-register-relatable-type.enum';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { CompanyProductPhase } from 'src/app/models/company_product_phase.model';
import { CalendarEventParticipantCategoryAssignment } from 'src/app/models/employee_calendar_register_user_shared_event.model';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { ApiService } from 'src/app/services/api.service';

export interface CalendarEventParticipantCategoryDialogData {
  participant_name: string;
  user_id?: number | null;
  assignment: CalendarEventParticipantCategoryAssignment | null;
  working_time_categories: WorkingTimeCategory[];
  all_working_time_categories: WorkingTimeCategory[];
}

@Component({
  selector: 'app-calendar-event-participant-category-dialog',
  templateUrl: './calendar-event-participant-category-dialog.component.html',
  styleUrl: './calendar-event-participant-category-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatButton,
    MatIconButton,
    MatIcon,
    MatSuffix,
  ],
})
export class CalendarEventParticipantCategoryDialogComponent implements OnInit {
  dialog_ref =
    inject<MatDialogRef<CalendarEventParticipantCategoryDialogComponent, CalendarEventParticipantCategoryAssignment | null>>(MatDialogRef);
  data = inject<CalendarEventParticipantCategoryDialogData>(MAT_DIALOG_DATA);
  private api_service = inject(ApiService);
  private form_builder = inject(UntypedFormBuilder);
  private change_detector_ref = inject(ChangeDetectorRef);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack = inject(MatSnackBar);

  form: UntypedFormGroup;
  company_product_control: UntypedFormControl = new UntypedFormControl('');
  company_product_phase_control: UntypedFormControl = new UntypedFormControl('');
  readonly filtered_company_products = signal<CompanyProduct[]>([]);
  readonly filtered_company_product_phases = signal<CompanyProductPhase[]>([]);
  selected_company_product: CompanyProduct | undefined;
  selected_company_product_phase: CompanyProductPhase | undefined;
  is_company_product_category = false;
  is_company_product_phase_category = false;
  readonly selected_working_time_categories = signal<WorkingTimeCategory[]>([]);
  readonly available_children_by_level = signal<WorkingTimeCategory[][]>([]);
  private readonly categories_by_id = new Map<number, WorkingTimeCategory>();

  constructor() {
    this.form = this.form_builder.group({
      working_time_category_level_0: [''],
      working_time_category_level_1: [''],
      working_time_category_level_2: [''],
      working_time_category_level_3: [''],
      working_time_category_level_4: [''],
      company_product_id: [''],
      company_product_phase_id: [''],
      relatable_type: [null as EmployeeCalendarRegisterRelatableType | null],
      relatable_id: [null as number | null],
      working_time_category: [null as WorkingTimeCategory | null],
    });
  }

  ngOnInit(): void {
    this.registerCategories([...this.data.working_time_categories, ...this.data.all_working_time_categories]);
    this.listenCompanyProductSearch();
    this.listenCompanyProductPhaseSearch();
    void this.hydrateFromAssignment();
  }

  displayFnCompanyProduct(product: CompanyProduct): string {
    return product ? product.title : '';
  }

  displayFnCompanyProductPhase(phase: CompanyProductPhase): string {
    return phase?.company_product_phase_type?.name ?? '';
  }

  onCategoryChange(category_id: number, level: number): void {
    const category = this.categories_by_id.get(Number(category_id));
    if (category == null) {
      return;
    }

    const selected = [...this.selected_working_time_categories().slice(0, level), category];
    this.selected_working_time_categories.set(selected);
    this.is_company_product_category = selected[0]?.slug === 'company-product';
    this.is_company_product_phase_category = category.needs_relation_when_registering_time === 'company_product_phase';
    this.form.patchValue({
      working_time_category: category,
      [`working_time_category_level_${level}`]: category.id,
    });

    if (!this.is_company_product_category) {
      this.resetCompanyProduct();
    } else if (!this.is_company_product_phase_category && this.form.value.company_product_id) {
      this.form.patchValue({
        relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProduct,
        relatable_id: this.form.value.company_product_id,
        company_product_phase_id: '',
      });
    } else {
      void this.autoSelectSoleProductIfNeeded();
    }

    if (category.slug !== 'company-product') {
      void this.loadChildren(category.id, level);
    }
  }

  selectCompanyProduct(company_product: CompanyProduct): void {
    this.selected_company_product = company_product;
    this.form.patchValue({
      company_product_id: company_product.id,
      relatable_id: this.is_company_product_phase_category ? this.form.value.company_product_phase_id : company_product.id,
      relatable_type: this.is_company_product_phase_category
        ? EmployeeCalendarRegisterRelatableType.CompanyProductPhase
        : EmployeeCalendarRegisterRelatableType.CompanyProduct,
    });
    const root = this.selected_working_time_categories()[0];
    if (root != null) {
      void this.loadChildren(root.id, 0);
    }
  }

  resetCompanyProduct(): void {
    this.selected_company_product = undefined;
    this.selected_company_product_phase = undefined;
    this.company_product_control.setValue('');
    this.company_product_phase_control.setValue('');
    this.form.patchValue({
      company_product_id: '',
      company_product_phase_id: '',
      relatable_type: null,
      relatable_id: null,
    });
  }

  selectCompanyProductPhase(phase: CompanyProductPhase): void {
    this.selected_company_product_phase = phase;
    this.form.patchValue({
      company_product_phase_id: phase.id,
      relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProductPhase,
      relatable_id: phase.id,
    });
  }

  resetCompanyProductPhase(): void {
    this.selected_company_product_phase = undefined;
    this.company_product_phase_control.setValue('');
    this.form.patchValue({
      company_product_phase_id: '',
      relatable_type: this.form.value.company_product_id ? EmployeeCalendarRegisterRelatableType.CompanyProduct : null,
      relatable_id: this.form.value.company_product_id || null,
    });
  }

  close(): void {
    this.dialog_ref.close(null);
  }

  save(): void {
    const assignment = this.buildAssignment();
    if (assignment.working_time_category_id == null) {
      return;
    }
    if (
      (this.is_company_product_category || assignment.working_time_category?.needs_relation_when_registering_time === 'company_product') &&
      !this.form.value.company_product_id
    ) {
      this.snack.open('Debes seleccionar un producto para esta categoría.', 'Ok', { duration: 3000 });
      return;
    }
    if (this.is_company_product_phase_category && !this.form.value.company_product_phase_id) {
      this.snack.open('Debes seleccionar una fase de producto para esta categoría.', 'Ok', { duration: 3000 });
      return;
    }
    this.dialog_ref.close(assignment);
  }

  private buildAssignment(): CalendarEventParticipantCategoryAssignment {
    const category = this.form.value.working_time_category as WorkingTimeCategory | null;
    return {
      working_time_category_id: category?.id ?? null,
      working_time_category: category,
      relatable_type: this.form.value.relatable_type ?? null,
      relatable_id: this.form.value.relatable_id ?? null,
      company_product_id: this.selected_company_product?.id ?? this.form.value.company_product_id ?? null,
      company_product_title: this.selected_company_product?.title ?? null,
      company_product_color: this.selected_company_product?.color ?? null,
      company_product_phase_name: this.selected_company_product_phase?.company_product_phase_type?.name ?? null,
    };
  }

  private async hydrateFromAssignment(): Promise<void> {
    const assignment = this.data.assignment;
    if (assignment?.working_time_category_id == null) {
      return;
    }

    const tree: WorkingTimeCategory[] = [];
    let category = this.categories_by_id.get(assignment.working_time_category_id) ?? assignment.working_time_category ?? null;
    if (category == null) {
      return;
    }
    tree.unshift(category);
    while (category.parent_category_slug != null) {
      const parent = [...this.categories_by_id.values()].find(item => item.slug === category!.parent_category_slug);
      if (parent == null) {
        break;
      }
      category = parent;
      tree.unshift(category);
    }

    if (assignment.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProduct && assignment.relatable_id) {
      this.selected_company_product = await lastValueFrom(this.api_service.getCompanyProduct(assignment.relatable_id.toString()));
      this.company_product_control.setValue(this.selected_company_product, { emitEvent: false });
      this.form.patchValue({
        company_product_id: this.selected_company_product.id,
        relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProduct,
        relatable_id: this.selected_company_product.id,
      });
    } else if (assignment.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProductPhase && assignment.relatable_id) {
      this.selected_company_product_phase = await lastValueFrom(this.api_service.getCompanyProductPhase(assignment.relatable_id));
      this.selected_company_product = await lastValueFrom(
        this.api_service.getCompanyProduct(this.selected_company_product_phase.company_product_id.toString())
      );
      this.company_product_control.setValue(this.selected_company_product, { emitEvent: false });
      this.company_product_phase_control.setValue(this.selected_company_product_phase, { emitEvent: false });
      this.form.patchValue({
        company_product_id: this.selected_company_product.id,
        company_product_phase_id: this.selected_company_product_phase.id,
        relatable_type: EmployeeCalendarRegisterRelatableType.CompanyProductPhase,
        relatable_id: this.selected_company_product_phase.id,
      });
    }

    this.is_company_product_category = tree[0]?.slug === 'company-product';
    if (this.is_company_product_category && !this.form.value.company_product_id) {
      await this.autoSelectSoleProductIfNeeded();
    }
    const selected: WorkingTimeCategory[] = [];
    for (let level = 0; level < tree.length; level++) {
      const tree_category = tree[level];
      selected.push(tree_category);
      this.selected_working_time_categories.set([...selected]);
      this.is_company_product_phase_category = tree_category.needs_relation_when_registering_time === 'company_product_phase';
      this.form.patchValue({
        [`working_time_category_level_${level}`]: tree_category.id,
        working_time_category: tree_category,
      });
      await this.loadChildren(tree_category.id, level);
    }
  }

  private async loadChildren(category_id: number, level: number): Promise<void> {
    const children = await lastValueFrom(
      this.api_service.getWorkingTimeCategoryChildren(category_id.toString(), {
        company_product_id: this.form.value.company_product_id,
      })
    );
    this.registerCategories(children);
    const available = [...this.available_children_by_level()];
    available[level] = children;
    this.available_children_by_level.set(available);
  }

  private registerCategories(categories: WorkingTimeCategory[]): void {
    for (const category of categories) {
      this.categories_by_id.set(category.id, category);
    }
  }

  private async autoSelectSoleProductIfNeeded(): Promise<void> {
    if (!this.is_company_product_category || this.form.value.company_product_id) {
      return;
    }

    const products = await this.loadParticipantProducts();
    this.filtered_company_products.set(products);
    if (products.length === 1) {
      this.selectCompanyProduct(products[0]);
      this.company_product_control.setValue(products[0], { emitEvent: false });
    }
    this.change_detector_ref.markForCheck();
  }

  private async loadParticipantProducts(): Promise<CompanyProduct[]> {
    if (this.data.user_id) {
      try {
        const user = await lastValueFrom(this.api_service.getUser(String(this.data.user_id), { append: 'active_company_products' }));
        return user.active_company_products ?? [];
      } catch {
        // Fall back to the products the current user can already see.
      }
    }

    const response = await lastValueFrom(this.api_service.getCompanyProducts({ scopes: 'isActive' }));
    return response.data ?? [];
  }

  private listenCompanyProductSearch(): void {
    this.company_product_control.valueChanges
      .pipe(
        startWith(''),
        debounceTime(200),
        map(search => (typeof search === 'string' ? search : '')),
        switchMap(query =>
          this.api_service.getCompanyProducts({ search: query }).pipe(
            map(response => response.data ?? []),
            catchError(() => of([] as CompanyProduct[]))
          )
        ),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(products => {
        this.filtered_company_products.set(products);
      });
  }

  private listenCompanyProductPhaseSearch(): void {
    this.company_product_phase_control.valueChanges
      .pipe(
        startWith(''),
        debounceTime(200),
        map(search => (typeof search === 'string' ? search : '')),
        switchMap(query =>
          this.api_service
            .getCompanyProductPhases({
              search: query,
              company_product_id: this.form.value.company_product_id,
              is_active: true,
            })
            .pipe(
              map(response => response.data ?? []),
              catchError(() => of([] as CompanyProductPhase[]))
            )
        ),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(phases => {
        this.filtered_company_product_phases.set(phases);
      });
  }
}
